import type { AcademicResult } from '@creanizer/types'
import { BookOpen, Users, Hash, FileText, Quote, Award } from 'lucide-react'

interface AcademicViewerProps {
  document: AcademicResult
}

export function AcademicViewer({ document }: AcademicViewerProps) {
  // データ検証
  if (!document || !document.title || !document.abstract) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">学術論文</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
      {/* ヘッダー（ジャーナル情報） */}
      {(document.metadata?.journal || document.metadata?.doi) && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6" />
              <div>
                {document.metadata.journal && (
                  <h2 className="text-lg font-medium">{document.metadata.journal}</h2>
                )}
                {document.metadata.volume && (
                  <p className="text-sm text-indigo-200">
                    Vol. {document.metadata.volume}
                    {document.metadata.issue && `, Issue ${document.metadata.issue}`}
                    {document.metadata.pages && `, pp. ${document.metadata.pages}`}
                  </p>
                )}
              </div>
            </div>
            {document.metadata.doi && (
              <div className="text-right">
                <p className="text-xs text-indigo-200">DOI</p>
                <p className="font-mono text-sm">{document.metadata.doi}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* 論文本体 */}
      <div className="p-8">
        {/* タイトル */}
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-6">
          {document.title}
        </h1>
        
        {/* 著者情報 */}
        <div className="mb-6 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-2">
            {document.authors.map((author, index) => (
              <div key={index} className="flex items-start gap-1">
                <span className="font-medium text-gray-900">
                  {author.name}
                  <sup className="text-xs text-blue-600">{index + 1}</sup>
                </span>
                {index < document.authors.length - 1 && (
                  <span className="text-gray-500">,</span>
                )}
              </div>
            ))}
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            {document.authors.map((author, index) => (
              <div key={index}>
                <sup>{index + 1}</sup> {author.affiliation}
              </div>
            ))}
          </div>
        </div>
        
        {/* キーワード */}
        {document.keywords && document.keywords.length > 0 && (
          <div className="mb-6 p-3 bg-gray-50 rounded">
            <div className="flex items-center gap-2 flex-wrap">
              <Hash className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Keywords:</span>
              {document.keywords.map((keyword, index) => (
                <span key={index} className="text-sm text-gray-600">
                  {keyword}{index < document.keywords.length - 1 && ','}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Abstract */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Abstract
          </h2>
          <div className="p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded">
            <p className="text-gray-700 text-justify leading-relaxed">
              {document.abstract}
            </p>
          </div>
        </div>
        
        {/* Introduction */}
        {document.introduction && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p className="text-gray-700 text-justify leading-relaxed">
              {document.introduction}
            </p>
          </div>
        )}
        
        {/* Methodology */}
        {document.methodology && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. Methodology</h2>
            <p className="text-gray-700 text-justify leading-relaxed">
              {document.methodology}
            </p>
          </div>
        )}
        
        {/* Results */}
        {document.results && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. Results</h2>
            <p className="text-gray-700 text-justify leading-relaxed">
              {document.results}
            </p>
          </div>
        )}
        
        {/* Discussion */}
        {document.discussion && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Discussion</h2>
            <p className="text-gray-700 text-justify leading-relaxed">
              {document.discussion}
            </p>
          </div>
        )}
        
        {/* Conclusion */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-3">5. Conclusion</h2>
          <p className="text-gray-700 text-justify leading-relaxed">
            {document.conclusion}
          </p>
        </div>
        
        {/* Acknowledgments */}
        {document.acknowledgments && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-600" />
              Acknowledgments
            </h2>
            <p className="text-gray-700 text-justify">
              {document.acknowledgments}
            </p>
          </div>
        )}
        
        {/* References */}
        {document.references && document.references.length > 0 && (
          <div className="pt-6 border-t border-gray-300">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Quote className="w-5 h-5 text-gray-600" />
              References
            </h2>
            <ol className="space-y-2 text-sm">
              {document.references.map((ref, index) => (
                <li key={index} className="flex gap-3">
                  <span className="font-medium text-gray-600">[{index + 1}]</span>
                  <span className="text-gray-700 text-justify">{ref.citation}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
      
      {/* フッター */}
      {document.metadata && (
        <div className="bg-gray-50 px-6 py-3 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>文字数: {document.metadata.wordCount}</span>
            <span>生成日時: {document.metadata.generatedAt}</span>
          </div>
        </div>
      )}
    </div>
  )
}